import React, { useCallback, useMemo, useRef } from 'react';

import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  type PointerSensorOptions,
  type UniqueIdentifier,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  type UseSortableArguments,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { type ListItemComponentType, SortableListItem } from './SortableListItem';

const defaultPointerSensorOptions: PointerSensorOptions = {
  activationConstraint: {
    distance: 8,
  },
};

export type SortableListProps<T, TElement extends HTMLElement = HTMLLIElement> = {
  items: T[];
  onItemsChanged(items: T[]): void;
  getId: (item: T) => UniqueIdentifier;
  ItemComponent: ListItemComponentType<T, TElement>;
  pointerSensorOptions?: PointerSensorOptions;
  disabled?: boolean;
  sortableProps?: Omit<UseSortableArguments, 'id'>;
  dndContextProps?: React.ComponentProps<typeof DndContext>;
};

export function SortableList<T, TElement extends HTMLElement = HTMLLIElement>({
  items,
  onItemsChanged,
  getId,
  ItemComponent,
  pointerSensorOptions,
  disabled = false,
  sortableProps,
  dndContextProps,
}: SortableListProps<T, TElement>) {
  const sensors = useSensors(
    useSensor(PointerSensor, pointerSensorOptions ?? defaultPointerSensorOptions),
  );

  const itemIds = useMemo(() => items.map(getId), [items, getId]);

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const itemIdsRef = useRef(itemIds);
  itemIdsRef.current = itemIds;

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const currentItems = itemsRef.current;
      const currentItemIds = itemIdsRef.current;

      const oldIndex = currentItemIds.indexOf(active.id);
      const newIndex = currentItemIds.indexOf(over.id);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const newItems = arrayMove(currentItems, oldIndex, newIndex);
        onItemsChanged(newItems);
      }
    },
    [onItemsChanged],
  );

  return (
    <DndContext
      sensors={disabled ? undefined : sensors}
      collisionDetection={closestCenter}
      onDragEnd={disabled ? undefined : handleDragEnd}
      {...dndContextProps}>
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        {items.map((item) => (
          <SortableListItem
            key={getId(item)}
            id={getId(item)}
            item={item}
            ItemComponent={ItemComponent}
            disabled={disabled}
            sortableProps={sortableProps}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}
